/**
 * useEntitlements — plan/entitlement gating for the UI.
 *
 * Reads the org's effective plan via the `my_entitlements` RPC (migration
 * 00139) once per org and caches it. The DATABASE is authoritative — every
 * premium feature is enforced server-side (`has_entitlement` / seat-cap RPCs).
 * This hook is for UX only: showing the plan, the seat meter, and lock badges /
 * upgrade walls so users aren't surprised by a server rejection.
 *
 * @since 1.0.0
 */
import { useAuth } from './useAuth';
import { usePlatformAdmin } from './usePlatformAdmin';
import { supabase } from '@/db/supabase';
import { featureKey, featureAllowed } from '@/shared/lib/entitlements';
import { createSharedRead } from '@/shared/lib/sharedRead';

export type BillingPhase = 'active' | 'grace' | 'warning' | 'readonly' | 'locked';

const PHASES: BillingPhase[] = ['active', 'grace', 'warning', 'readonly', 'locked'];

/**
 * Platform-admin-only dunning PREVIEW: `?previewPhase=readonly` (etc.) lets an
 * admin eyeball the grace/warning/read-only/locked UI without a second account
 * (org_billing_phase bypasses admins server-side, so they'd never see it). This
 * is display-only — server enforcement ignores all client state. Returns null
 * for absent/invalid values.
 */
function readPreviewPhase(): BillingPhase | null {
    if (typeof window === 'undefined') return null;
    const p = new URLSearchParams(window.location.search).get('previewPhase');
    return p && (PHASES as string[]).includes(p) ? (p as BillingPhase) : null;
}

export interface Entitlements {
    planName: string;
    status: string;
    billingCycle: string;
    currentPeriodEnd: string | null;
    priceMonthlyInr: number | null;
    phase: BillingPhase;
    daysOverdue: number;
    /** TRUE auto-renew: a Razorpay Subscription is attached AND the sub is active. */
    autoRenew: boolean;
    /** Razorpay subscription id (sub_…) when auto-renew is set up, else null. */
    gatewaySubscriptionId: string | null;
    limits: { seats: number | null; max_orgs: number | null; max_transactions_per_month: number | null };
    seatsUsed: number;
    features: Record<string, boolean>;
}


function parse(data: unknown): Entitlements | null {
    if (!data || typeof data !== 'object') return null;
    const d = data as Record<string, unknown>;
    if (!d.plan_name) return null;
    const limits = (d.limits ?? {}) as Record<string, unknown>;
    const used = (d.used ?? {}) as Record<string, unknown>;
    return {
        planName: String(d.plan_name),
        status: String(d.status ?? 'none'),
        billingCycle: String(d.billing_cycle ?? 'monthly'),
        currentPeriodEnd: (d.current_period_end as string) ?? null,
        priceMonthlyInr: (d.price_monthly_inr as number) ?? null,
        phase: (d.phase as BillingPhase) ?? 'active',
        daysOverdue: (d.days_overdue as number) ?? 0,
        autoRenew: (d.auto_renew as boolean) ?? false,
        gatewaySubscriptionId: (d.gateway_subscription_id as string) ?? null,
        limits: {
            seats: (limits.seats as number) ?? null,
            max_orgs: (limits.max_orgs as number) ?? null,
            max_transactions_per_month: (limits.max_transactions_per_month as number) ?? null,
        },
        seatsUsed: (used.seats as number) ?? 0,
        features: (d.features ?? {}) as Record<string, boolean>,
    };
}

// One answer per org for the whole app, one call in flight (fastphone-2): the
// old module cache was checked before the first answer landed, so the cold open
// asked `my_entitlements` four times. null = asked, this org has no plan row.
const entitlementsRead = createSharedRead<Entitlements | null>(async (orgId) => {
    const { data } = await supabase.rpc('my_entitlements', { p_org_id: orgId });
    return parse(data);
});

export function useEntitlements() {
    const orgId = useAuth((s) => s.currentOrg?.id) ?? '';
    const isAdmin = usePlatformAdmin();
    // Admin-only display override (see readPreviewPhase). Never affects non-admins
    // or server enforcement.
    const preview = isAdmin ? readPreviewPhase() : null;

    const read = entitlementsRead.use(orgId || null);
    const ent = read.value ?? null;
    const loading = !!orgId && read.loading;

    const has = (key: string): boolean => ent?.features?.[featureKey(key)] ?? false;
    // Missing key ⇒ allowed (Sec. 4): only a plan that lists the key as false withholds
    // it. Drives the module SKU rail — inert until the founder's pricing migration.
    const allowed = (key: string): boolean => featureAllowed(key, ent?.features ?? {});
    const seatsMax = ent?.limits?.seats ?? null;
    const seatsUsed = ent?.seatsUsed ?? 0;

    return {
        entitlements: ent,
        loading,
        plan: ent?.planName ?? null,
        status: ent?.status ?? null,
        phase: (preview ?? ent?.phase ?? 'active') as BillingPhase,
        // Give the preview banner a sane countdown (BillingGate computes lockIn = 30 - daysOverdue).
        daysOverdue: preview ? (preview === 'readonly' ? 20 : preview === 'warning' ? 25 : 0) : (ent?.daysOverdue ?? 0),
        /** Set (admin only) when a ?previewPhase= override is active — display is simulated, not real. */
        previewPhase: preview,
        isPlatformAdmin: isAdmin,
        /** Does the org's plan include this feature? Accepts 'manufacturing' or 'feature:manufacturing'. */
        has,
        /** Is this feature allowed? Missing key ⇒ allowed (Sec. 4); only an explicit false withholds it. */
        allowed,
        seatsUsed,
        seatsMax,
        atSeatLimit: seatsMax != null && seatsUsed >= seatsMax,
        /** TRUE auto-renew is on (Razorpay Subscription attached + active). */
        autoRenew: ent?.autoRenew ?? false,
        /** Razorpay subscription id when auto-renew is set up, else null. */
        gatewaySubscriptionId: ent?.gatewaySubscriptionId ?? null,
        /** Ask again (call after an upgrade); every reader gets the new plan. */
        refresh: () => { if (orgId) entitlementsRead.refresh(orgId); },
    };
}
